import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import SEO from './SEO';
import { trackShipment, consumeCredits } from '../services/api';
import { saveTrackingRequest } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import Header from './Header';
import Footer from './Footer';
import InfoCards from './InfoCards';
import './TrackingInterface.css';

/* ─── SVG Icons ─────────────────────────────────────────────────────────────── */
const SearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
    <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
  </svg>
);

const PlaneIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
    <path d="M6.428 1.151C6.708.591 7.213 0 8 0s1.292.592 1.572 1.151C9.861 1.73 10 2.431 10 3v3.691l5.17 2.585a1.5 1.5 0 0 1 .83 1.342V12a.5.5 0 0 1-.582.493l-5.507-.918-.375 2.253 1.318 1.318A.5.5 0 0 1 10.5 16h-5a.5.5 0 0 1-.354-.854l1.319-1.318-.376-2.253-5.507.918A.5.5 0 0 1 0 12v-1.382a1.5 1.5 0 0 1 .83-1.342L6 6.691V3c0-.568.14-1.271.428-1.849Z" />
  </svg>
);

const PinIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
  </svg>
);

/* ─── Inline Styles ──────────────────────────────────────────────────────────── */
const S = {
  /* layout */
  page: { fontFamily: "var(--font-body)", background: '#F8FAFB', color: '#1A2332', minHeight: '100vh' },

  /* hero */
  hero: {
    background: '#ffffff',
    padding: '52px 48px 60px',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    borderBottom: '1px solid #E2E8F0',
  },
  heroLabel: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: 'rgba(27,153,139,0.1)', border: '1px solid rgba(27,153,139,0.25)',
    color: '#1B998B', fontSize: 11, fontWeight: 600, letterSpacing: '.08em',
    textTransform: 'uppercase', padding: '4px 12px', borderRadius: 20, marginBottom: 18,
  },
  heroH1: {
    fontFamily: "var(--font-heading)", fontSize: 38, fontWeight: 800,
    color: '#022C43', lineHeight: 1.15, marginBottom: 8,
  },
  heroP: { color: '#64748B', fontSize: 14, marginBottom: 32, fontWeight: 400 },

  /* search row */
  searchRow: { display: 'flex', gap: 10, maxWidth: 800, margin: '0 auto', position: 'relative', zIndex: 1 },
  searchBox: {
    flex: 1, display: 'flex', alignItems: 'center', gap: 10,
    background: '#fff', border: '1.5px solid #c8e8e6',
    borderRadius: 12, padding: '0 16px',
    boxShadow: '0 2px 12px rgba(27,58,92,0.07)',
  },
  searchInput: {
    flex: 1, background: 'none', border: 'none', outline: 'none',
    color: '#1A2332', fontFamily: "var(--font-body)", fontSize: 15, padding: '16px 0',
    letterSpacing: '.02em',
  },
  selectWrap: {
    display: 'flex', alignItems: 'center', gap: 8,
    background: '#fff', border: '1.5px solid #c8e8e6',
    borderRadius: 12, padding: '0 4px 0 14px', minWidth: 210,
    position: 'relative', zIndex: 10,
    boxShadow: '0 2px 12px rgba(27,58,92,0.07)',
  },
  btnTrack: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '0 26px',
    background: '#022C43', color: '#fff', border: 'none', borderRadius: 12,
    fontFamily: "var(--font-body)", fontSize: 14, fontWeight: 700,
    cursor: 'pointer', whiteSpace: 'nowrap', minHeight: 54, flexShrink: 0,
  },

  /* main area */
  main: { maxWidth: 1100, margin: '0 auto', padding: '40px 48px 80px' },

  /* status banner */
  statusBanner: {
    background: 'white', border: '1px solid #E2E8F0', borderRadius: 16,
    padding: '24px 28px', marginBottom: 28,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24,
    boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
    animation: 'slideUp .4s ease both',
  },
  statusLeft: { display: 'flex', alignItems: 'center', gap: 20 },
  statusId: { fontFamily: "var(--font-heading)", fontSize: 18, fontWeight: 700 },
  statusIdSub: { fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 400, color: '#64748B', display: 'block', marginTop: 2 },
  routePill: {
    display: 'flex', alignItems: 'center', gap: 10,
    background: '#F8FAFB', border: '1px solid #E2E8F0',
    borderRadius: 30, padding: '8px 16px', fontSize: 13, fontWeight: 500,
  },

  /* meta cards */
  metaRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 28 },
  metaCard: {
    background: 'white', border: '1px solid #E2E8F0', borderRadius: 16,
    padding: '20px 24px', boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
  },
  metaLabel: { fontSize: 11, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: '#94A3B8', marginBottom: 8 },
  metaValue: { fontFamily: "var(--font-subheading)", fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 },

  /* progress */
  progressSection: { marginBottom: 28 },
  progressTrack: {
    background: 'white', border: '1px solid #E2E8F0', borderRadius: 16,
    padding: '24px 28px', boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
  },
  progressLabel: { display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 },
  progressBarWrap: { background: '#F8FAFB', borderRadius: 20, height: 6, overflow: 'visible', position: 'relative' },
  progressStops: { display: 'flex', justifyContent: 'space-between', marginTop: 16 },
  stop: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, fontSize: 11, color: '#94A3B8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.04em' },

  /* details grid */
  detailsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 28 },
  detailCard: { background: 'white', border: '1px solid #E2E8F0', borderRadius: 16, padding: '20px 24px', boxShadow: '0 1px 6px rgba(0,0,0,0.04)' },

  /* timeline section */
  timelineSection: {
    background: 'white', border: '1px solid #E2E8F0', borderRadius: 16,
    padding: '32px 28px', boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
  },
  sectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 },
  sectionTitle: { fontFamily: "var(--font-heading)", fontSize: 20, fontWeight: 700 },
  eventCount: { background: 'rgba(13,148,136,0.1)', color: '#0D9488', fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 20 },

  /* timeline row */
  timelineItem: { display: 'grid', gridTemplateColumns: '160px 32px 1fr', gap: '0 16px', minHeight: 80 },
  timelineDate: { textAlign: 'right', paddingTop: 2, paddingRight: 4 },
  tlDate: { fontSize: 12, fontWeight: 600, color: '#0D9488', whiteSpace: 'nowrap' },
  tlTime: { fontSize: 11, color: '#94A3B8', whiteSpace: 'nowrap' },
  tlMeta: { marginTop: 8, fontSize: 11, color: '#64748B', lineHeight: 1.5 },
  timelineSpine: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  spineLine: { width: 2, flex: 1, background: '#E2E8F0', marginTop: 4 },
  tlEvent: { fontSize: 14, fontWeight: 500, marginBottom: 4, lineHeight: 1.4 },
  tlLocation: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 500, color: '#94A3B8', letterSpacing: '.02em', textTransform: 'uppercase' },

  /* error / no results */
  errorBox: { background: '#FFF1F2', border: '1px solid #FECDD3', borderRadius: 16, padding: '20px 24px', marginTop: 20 },
  noResults: { textAlign: 'center', padding: '48px 24px', color: '#64748B' },

  /* external links */
  externalSection: { marginTop: 28 },
  extLink: {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 20px',
    background: 'rgba(13,148,136,0.08)', border: '1.5px solid rgba(13,148,136,0.25)',
    borderRadius: 10, color: '#0D9488', textDecoration: 'none', fontSize: 14, fontWeight: 600, marginRight: 10,
  },

  /* loading spinner */
  spinner: {
    width: 18, height: 18, border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: 'white', borderRadius: '50%',
    animation: 'spin .7s linear infinite', display: 'inline-block',
  },

  carrierLogo: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 28, height: 28, background: '#0D9488', borderRadius: 6,
    color: 'white', fontSize: 9, fontWeight: 800, letterSpacing: '-.5px',
    fontFamily: "var(--font-heading)", flexShrink: 0,
  },
};

/* ─── Status badge colours ───────────────────────────────────────────────────── */
const badgeStyle = (code) => {
  const map = {
    delivered: { background: '#F0FDF4', border: '1.5px solid #BBF7D0', color: '#15803D' },
    transit:   { background: '#FFF7ED', border: '1.5px solid #FED7AA', color: '#C2410C' },
    processing:{ background: '#EFF6FF', border: '1.5px solid #BFDBFE', color: '#1D4ED8' },
    unknown:   { background: '#F8FAFC', border: '1.5px solid #E2E8F0', color: '#64748B' },
  };
  return {
    ...(map[code] || map.unknown),
    display: 'inline-flex', alignItems: 'center', gap: 7,
    fontSize: 12, fontWeight: 700, letterSpacing: '.08em',
    textTransform: 'uppercase', padding: '6px 14px', borderRadius: 20, flexShrink: 0,
  };
};

/* ─── Keyframe injection ─────────────────────────────────────────────────────── */
const cssRules = `
  @keyframes slideUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes ping    { 0% { transform:scale(1); opacity:.45; } 80%,100% { transform:scale(2.4); opacity:0; } }
  .spine-dot-active { position:relative; }
  .spine-dot-active::before, .spine-dot-active::after {
    content:''; position:absolute; inset:-4px; border-radius:50%;
    background:#0D9488; opacity:.4; animation: ping 1.6s cubic-bezier(0,0,.2,1) infinite;
    pointer-events:none;
  }
  .spine-dot-active::after { inset:-8px; opacity:.2; animation-delay:.3s; }
  .stop-dot-current { position:relative; }
  .stop-dot-current::before, .stop-dot-current::after {
    content:''; position:absolute; inset:-3px; border-radius:50%;
    background:#0D9488; opacity:.4; animation: ping 1.6s cubic-bezier(0,0,.2,1) infinite;
    pointer-events:none;
  }
  .stop-dot-current::after { inset:-6px; opacity:.2; animation-delay:.3s; }
  .btn-track-hover:hover { background:#E6FAF8 !important; transform:translateY(-1px); }
  .ext-link-hover:hover { background:rgba(13,148,136,0.15) !important; }
  .ti-hero::before {
    content:''; position:absolute; top:-80px; right:-80px;
    width:320px; height:320px; border-radius:50%;
    background:radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 65%);
    pointer-events:none;
  }
  .ti-hero::after {
    content:''; position:absolute; bottom:-50px; left:15%;
    width:240px; height:240px; border-radius:50%;
    background:radial-gradient(circle, rgba(255,255,255,0.07) 0%, transparent 65%);
    pointer-events:none;
  }
`;

/* ═══════════════════════════════════════════════════════════════════════════════
   COMPONENT
══════════════════════════════════════════════════════════════════════════════════ */
function TrackingInterface() {
  const { requestTrackingAccess, updateCreditBalance, user, isAuthInitialized, isWhitelistedUser } = useAuth();

  const [trackingNumber, setTrackingNumber] = useState('');
  const [isLoading, setIsLoading]           = useState(false);
  const [trackingResult, setTrackingResult] = useState(null);
  const [error, setError]                   = useState(null);
  const [hasSearched, setHasSearched]       = useState(false);
  const [searchParams]                      = useSearchParams();
  const didAutoSearch                        = useRef(false);

  /* auto-search from URL params */
  useEffect(() => {
    if (!isAuthInitialized || didAutoSearch.current) return;
    const urlTracking = searchParams.get('tracking');
    if (!urlTracking) return;
    didAutoSearch.current = true;

    setTrackingNumber(urlTracking);

    const run = async () => {
      setIsLoading(true); setTrackingResult(null); setError(null); setHasSearched(true);
      try {
        const data = await trackShipment(urlTracking);
        if (!data || !Array.isArray(data.shipments)) throw new Error('Invalid response format from tracking service');
        if (user) {
          if (!isWhitelistedUser && data.shipments.length > 0) {
            const { creditBalance } = await consumeCredits(user.uid, urlTracking);
            updateCreditBalance(creditBalance);
          }
          saveTrackingRequest(user.uid, urlTracking).catch(() => {});
        }
        setTrackingResult(data);
      } catch (err) {
        setError(err.message || 'Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    requestTrackingAccess(run);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthInitialized]);

  const handleSubmit = (e) => { e.preventDefault(); requestTrackingAccess(performSearch); };

  const performSearch = async () => {
    setIsLoading(true); setTrackingResult(null); setError(null); setHasSearched(true);
    try {
      const data = await trackShipment(trackingNumber);
      if (!data || !Array.isArray(data.shipments)) throw new Error('Invalid response format from tracking service');
      if (user) {
        if (!isWhitelistedUser && data.shipments.length > 0) {
          const { creditBalance } = await consumeCredits(user.uid, trackingNumber);
          updateCreditBalance(creditBalance);
        }
        saveTrackingRequest(user.uid, trackingNumber).catch(() => {});
      }
      setTrackingResult(data);
    } catch (err) {
      setError(err.message || 'Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  /* ── helpers (unchanged logic) ── */
  const formatDate = (dateString) => {
    try {
      const d = new Date(dateString);
      return d.toLocaleString('en-US', { year:'numeric', month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' });
    } catch { return 'Invalid Date'; }
  };

  const getAttributeValue = (shipment, label) => {
    if (!shipment?.attributes) return 'N/A';
    return shipment.attributes.find(a => a?.l === label)?.val || 'N/A';
  };

  const getShipmentStates = (shipment) => {
    if (!Array.isArray(shipment?.states)) return [];
    return [...shipment.states]
      .filter(Boolean)
      .sort((left, right) => new Date(right?.date || 0) - new Date(left?.date || 0));
  };

  const getLatestState = (shipment) => getShipmentStates(shipment)[0] || null;

  const getDisplayValue = (value) => {
    if (!value) return 'N/A';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      const parts = [value.port, value.location, value.name, value.country].filter(Boolean);
      return parts.join(', ') || 'N/A';
    }
    return 'N/A';
  };

  const getTrackingDisplay = (shipment) =>
    shipment?.trackingId || shipment?.container?.number || getAttributeValue(shipment, 'tracking_id');

  const getContainerNumber = (shipment) => shipment?.container?.number || getTrackingDisplay(shipment);

  const getPOLDisplay = (shipment) => {
    if (!shipment) return 'N/A';
    if (shipment.pol) return getDisplayValue(shipment.pol);
    const fromAttr = getAttributeValue(shipment, 'from');
    return fromAttr !== 'N/A' ? fromAttr : getDisplayValue(shipment.origin);
  };

  const getPODDisplay = (shipment) => {
    if (!shipment) return 'N/A';
    if (shipment.pod) return getDisplayValue(shipment.pod);
    const toAttr = getAttributeValue(shipment, 'to');
    return toAttr !== 'N/A' ? toAttr : getDisplayValue(shipment.destination);
  };

  const getStateLocation = (state) => {
    if (!state) return 'N/A';
    const location = state.location || '';
    const country = state.country || '';
    if (!location && !country) return 'N/A';
    if (location && country && location.toLowerCase().includes(country.toLowerCase())) return location;
    return [location, country].filter(Boolean).join(', ');
  };

  const getOriginDisplay = (shipment) => {
    if (!shipment) return 'N/A';
    return getPOLDisplay(shipment);
  };

  const getDestinationDisplay = (shipment) => {
    if (!shipment) return 'N/A';
    const latestLocation = getStateLocation(getLatestState(shipment));
    if (latestLocation !== 'N/A') return latestLocation;
    return getPODDisplay(shipment);
  };

  const getCarrierDisplay = (shipment) => {
    if (!shipment) return 'N/A';
    if (Array.isArray(shipment.carriers) && shipment.carriers.length > 0) return shipment.carriers.join(', ');
    if (typeof shipment.carrier === 'string' && shipment.carrier) return shipment.carrier;
    return getAttributeValue(shipment, 'carrier');
  };

  const getContainerType = (shipment) => {
    if (!shipment) return 'N/A';
    if (shipment.container && typeof shipment.container === 'object') return shipment.container.type || 'N/A';
    return getAttributeValue(shipment, 'container_type');
  };

  const getScacDisplay = (shipment) => {
    if (Array.isArray(shipment?.scacs) && shipment.scacs.length > 0) return shipment.scacs.join(', ');
    return getAttributeValue(shipment, 'operator');
  };

  const getVesselDisplay = (shipment) => getLatestState(shipment)?.vessel || 'N/A';

  const getVoyageDisplay = (shipment) => getLatestState(shipment)?.voyage || 'N/A';

  const getTerminalDisplay = (shipment) => getLatestState(shipment)?.terminal || 'N/A';

  const getShipmentStatus = (shipment) => {
    const latestState = getLatestState(shipment);
    const statusValue = shipment?.shipmentStatus || getAttributeValue(shipment, 'shipment_status');
    const rawStatus = String(statusValue || latestState?.status || '').toLowerCase();

    if (!rawStatus) return { code: 'unknown', display: 'Unknown' };

    try {
      if (rawStatus.includes('delivered') || rawStatus.includes('gate out')) {
        return { code: 'delivered', display: 'Delivered' };
      }
      if (
        rawStatus.includes('transit') ||
        rawStatus.includes('loaded') ||
        rawStatus.includes('discharged') ||
        rawStatus.includes('delay') ||
        rawStatus.includes('arrival') ||
        rawStatus.includes('depart')
      ) {
        return { code: 'transit', display: 'In Transit' };
      }
      if (rawStatus.includes('pending') || rawStatus.includes('processing') || rawStatus.includes('booked')) {
        return { code: 'processing', display: 'Processing' };
      }

      const display = statusValue
        ? String(statusValue).replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
        : 'Unknown';

      return { code: rawStatus, display };
    } catch { return { code: 'unknown', display: 'Unknown' }; }
  };

  const calculateProgress = (shipment) => {
    if (!shipment) return 0;
    try {
      const latestState = getLatestState(shipment);
      const progressByCode = {
        CEP: 10,
        CGI: 20,
        CLL: 40,
        CDT: 60,
        UNK: 65,
        CLT: 75,
        CDD: 90,
        CDC: 100,
      };
      const stateCode = latestState?.statusCode;
      if (stateCode && progressByCode[stateCode] !== undefined) return progressByCode[stateCode];

      const code = getShipmentStatus(shipment).code;
      if (code === 'delivered') return 100;
      if (code === 'transit') return 65;
      if (code === 'processing') return 20;
      return 10;
    } catch { return 10; }
  };

  /* ── Port code helpers (use `from`/`to` attrs when available) ── */
  const getPortFrom = (shipment) => {
    const attr = getAttributeValue(shipment, 'from');
    return attr !== 'N/A' ? attr : getPOLDisplay(shipment);
  };

  const getPortTo = (shipment) => {
    const attr = getAttributeValue(shipment, 'to');
    return attr !== 'N/A' ? attr : getPODDisplay(shipment);
  };

  /* ── derived for progress stops ── */
  const buildProgressStops = (shipment) => {
    if (!getShipmentStates(shipment).length) return null;
    const pct  = calculateProgress(shipment);
    const from = getPOLDisplay(shipment);
    const to   = getPODDisplay(shipment);
    return { pct, from, to, current: getDestinationDisplay(shipment) };
  };

  /* ── Last updated text ── */
  const getLastUpdated = (shipment) =>
    getLatestState(shipment)?.date ? `Last updated: ${formatDate(getLatestState(shipment).date)}` : '';

  const getExternalTrackingLinks = (shipment) => {
    if (Array.isArray(shipment?.externalTracking) && shipment.externalTracking.length > 0) {
      return shipment.externalTracking;
    }
    if (shipment?.carrierUrl) {
      return [{ title: `${getCarrierDisplay(shipment)} tracking`, url: shipment.carrierUrl }];
    }
    return [];
  };

  const getErrorDetail = (message) =>
    String(message || '').toLowerCase().includes('network')
      ? 'Check your connection and submit the request again.'
      : 'The tracking service is unavailable right now.';

  /* ─────────────────────────────────────────────────────────────────────────── */
  return (
    <div style={S.page}>
      <SEO
        title="Track Your Shipment"
        description="Get live tracking status, ETA, and port-by-port journey timeline for your ocean container. Powered by real-time carrier data."
        canonicalPath="/results"
      />
      <style>{cssRules}</style>
      <Header />

      {/* ── HERO / SEARCH ── */}
      <div style={S.hero} className="ti-hero">
        <div style={S.heroLabel}>
          <svg width="8" height="8" viewBox="0 0 8 8"><circle cx="4" cy="4" r="4" fill="currentColor"/></svg>
          Live Tracking
        </div>
        <h1 style={S.heroH1}>Track Your Shipment</h1>
        <p  style={S.heroP}>Enter your tracking number to fetch the latest shipment status</p>

        <form onSubmit={handleSubmit}>
          <div style={S.searchRow} className="ti-search-row">
            {/* tracking number */}
            <div style={S.searchBox} className="ti-search-box">
              <span style={{ color: '#94A3B8', flexShrink: 0 }}><SearchIcon /></span>
              <input
                style={S.searchInput}
                type="text"
                value={trackingNumber}
                onChange={e => setTrackingNumber(e.target.value)}
                placeholder="Enter tracking number"
                required
              />
            </div>
            {/* submit */}
            <button type="submit" className="btn-track-hover ti-btn-track" style={S.btnTrack} disabled={isLoading}>
              {isLoading
                ? <span style={S.spinner} />
                : <><SearchIcon /><span>Track Package</span></>
              }
            </button>
          </div>
        </form>
      </div>

      {/* ── MAIN ── */}
      <div style={S.main} className="ti-main">

        {/* Error */}
        {error && (
          <div style={S.errorBox}>
            <p style={{ color: '#BE123C', fontWeight: 600, marginBottom: 6 }}>{error}</p>
            <p style={{ color: '#9F1239', fontSize: 13 }}>
              {getErrorDetail(error)}
            </p>
          </div>
        )}

        {/* Info cards when no search yet */}
        {!hasSearched && !error && <InfoCards isVisible={true} />}

        {/* No results */}
        {hasSearched && trackingResult && (!trackingResult.shipments || trackingResult.shipments.length === 0) && (
          <div style={S.noResults}>
            <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 700, marginBottom: 8 }}>No shipments found</p>
            <p style={{ fontSize: 14 }}>Please check the tracking number and try again.</p>
          </div>
        )}

        {/* Results */}
        {trackingResult?.shipments?.map((shipment, idx) => {
          const status   = getShipmentStatus(shipment);
          const progress = buildProgressStops(shipment);
          const pct      = progress?.pct ?? 0;
          const states   = getShipmentStates(shipment);
          const carrier  = getCarrierDisplay(shipment);
          const carrierInitials = carrier !== 'N/A' ? carrier.split(' ').map(w => w[0]).join('').slice(0,3) : '?';
          const detailCards = [
            { label: 'Container Number', value: getContainerNumber(shipment) },
            { label: 'Container Type', value: getContainerType(shipment) },
            { label: 'SCAC', value: getScacDisplay(shipment) },
            { label: 'Port of Loading', value: getPOLDisplay(shipment) },
            { label: 'Port of Discharge', value: getPODDisplay(shipment) },
            { label: 'Current Vessel', value: getVesselDisplay(shipment) },
            { label: 'Voyage', value: getVoyageDisplay(shipment) },
            { label: 'Terminal', value: getTerminalDisplay(shipment) },
          ].filter((item) => item.value && item.value !== 'N/A');
          const externalTrackingLinks = getExternalTrackingLinks(shipment);

          return (
            <div key={idx} style={{ animation: 'slideUp .4s ease both' }}>

              {/* STATUS BANNER */}
              <div style={S.statusBanner} className="ti-status-banner">
                <div style={S.statusLeft}>
                  <span style={badgeStyle(status.code)}>
                    {status.code === 'transit' && (
                      <span style={{ width: 6, height: 6, background: '#F97316', borderRadius: '50%',
                        animation: 'ping 1.8s ease-in-out infinite', display: 'inline-block' }} />
                    )}
                    {status.display}
                  </span>
                  <div style={S.statusId}>
                    {getTrackingDisplay(shipment)}
                    <span style={S.statusIdSub}>{getLastUpdated(shipment)}</span>
                  </div>
                </div>
                <div style={S.routePill}>
                  <span>{getPortFrom(shipment)}</span>
                  <span style={{ color: '#0D9488', fontSize: 16 }}>→</span>
                  <span>{getPortTo(shipment)}</span>
                </div>
              </div>

              {/* META CARDS */}
              <div style={S.metaRow} className="ti-meta-row">
                <div style={S.metaCard}>
                  <div style={S.metaLabel}>Origin</div>
                  <div style={S.metaValue}>
                    <PinIcon />
                    {getOriginDisplay(shipment)}
                  </div>
                </div>
                <div style={S.metaCard}>
                  <div style={S.metaLabel}>Current Location</div>
                  <div style={S.metaValue}>
                    <PinIcon />
                    {getDestinationDisplay(shipment)}
                  </div>
                </div>
                <div style={S.metaCard}>
                  <div style={S.metaLabel}>Carrier</div>
                  <div style={S.metaValue}>
                    <div style={S.carrierLogo}>{carrierInitials}</div>
                    {carrier}
                  </div>
                </div>
              </div>

              {/* DETAILS GRID */}
              {detailCards.length > 0 && (
                <div style={{ ...S.detailsGrid, marginBottom: 28 }} className="ti-details-grid">
                  {detailCards.map((item) => (
                    <div key={item.label} style={S.detailCard}>
                      <div style={S.metaLabel}>{item.label}</div>
                      <div style={{ ...S.metaValue, fontSize: 15 }}>{item.value}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* PROGRESS BAR */}
              {progress && (() => {
                const stopDefs = [
                  { label: progress.from !== 'N/A' ? progress.from : 'Origin' },
                  { label: 'Loaded' },
                  { label: 'Transshipment' },
                  { label: progress.to !== 'N/A' ? progress.to : progress.current },
                  { label: 'Delivered' },
                ];
                const thresholds = [0, 20, 40, 70, 100];

                let currentIdx = 0;
                for (let i = thresholds.length - 1; i >= 0; i--) {
                  if (pct >= thresholds[i]) { currentIdx = i; break; }
                }
                const barFill = (currentIdx / (stopDefs.length - 1)) * 100;

                return (
                  <div style={S.progressSection}>
                    <div style={S.progressTrack}>
                      <div style={S.progressLabel}>
                        <span>Shipment Progress</span>
                        <span>{pct}% complete</span>
                      </div>

                      {/* Bar track + dots overlaid */}
                      <div style={{ position: 'relative', paddingBottom: 32 }}>
                        {/* Grey track + teal fill */}
                        <div style={{ height: 6, background: '#F1F5F9', borderRadius: 20, overflow: 'hidden' }}>
                          <div style={{
                            height: '100%', background: 'linear-gradient(90deg, #0D9488, #14B8A6)',
                            borderRadius: 20, width: `${barFill}%`, transition: 'width .6s ease',
                          }} />
                        </div>

                        {/* Dots + labels sitting on the bar */}
                        {stopDefs.map(({ label }, i) => {
                          const isDone    = i < currentIdx;
                          const isCurrent = i === currentIdx;
                          const dotSize   = isCurrent ? 14 : 10;
                          const leftPct   = (i / (stopDefs.length - 1)) * 100;
                          const labelX    = i === 0 ? 'translateX(0)' : i === stopDefs.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)';
                          return (
                            <React.Fragment key={i}>
                              <div
                                className={isCurrent ? 'stop-dot-current' : undefined}
                                style={{
                                  position: 'absolute', left: `${leftPct}%`, top: 3,
                                  transform: 'translate(-50%, -50%)',
                                  width: dotSize, height: dotSize, borderRadius: '50%',
                                  background: (isDone || isCurrent) ? '#0D9488' : '#CBD5E1',
                                  border: '2px solid white', zIndex: 2,
                                }}
                              />
                              <div style={{
                                position: 'absolute', left: `${leftPct}%`, top: 18,
                                transform: labelX,
                                fontSize: 10, fontWeight: isCurrent ? 700 : 500,
                                color: isDone ? '#0D9488' : isCurrent ? '#1A2332' : '#94A3B8',
                                whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '.04em',
                              }}>
                                {label}
                              </div>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* TIMELINE */}
              {states.length > 0 && (
                <div style={S.timelineSection}>
                  <div style={S.sectionHeader}>
                    <h2 style={S.sectionTitle}>Tracking History</h2>
                    <span style={S.eventCount}>{states.length} events</span>
                  </div>

                  <div>
                    {states.map((state, si) => {
                      const isActive = si === 0;
                      const d = new Date(state?.date || '');
                      const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                      const timeStr = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                      const meta = [
                        state?.terminal ? `Terminal ${state.terminal}` : null,
                        state?.vessel ? `Vessel ${state.vessel}` : null,
                        state?.voyage ? `Voyage ${state.voyage}` : null,
                      ].filter(Boolean).join('  •  ');

                      return (
                        <div key={si} style={S.timelineItem} className="ti-timeline-item">
                          {/* date */}
                          <div style={S.timelineDate}>
                            <div style={S.tlDate}>{dateStr}</div>
                            <div style={S.tlTime}>{timeStr}</div>
                          </div>

                          {/* spine */}
                          <div style={S.timelineSpine}>
                            {isActive ? (
                              <div className="spine-dot-active" style={{
                                width: 14, height: 14, borderRadius: '50%',
                                background: '#0D9488', border: '2px solid white',
                                flexShrink: 0, marginTop: 4, zIndex: 1,
                              }} />
                            ) : (
                              <div style={{
                                width: 12, height: 12, borderRadius: '50%',
                                background: '#E2E8F0', border: '2px solid white',
                                flexShrink: 0, marginTop: 4, zIndex: 1,
                              }} />
                            )}
                            {si < states.length - 1 && <div style={S.spineLine} />}
                          </div>

                          {/* content */}
                          <div style={{ paddingBottom: 28 }}>
                            <div style={{
                              ...S.tlEvent,
                              color: isActive ? '#0D9488' : '#1A2332',
                              fontWeight: isActive ? 600 : 500,
                            }}>
                              {state?.status?.includes('Flight') && (
                                <span style={{ marginRight: 6, opacity: .7 }}><PlaneIcon /></span>
                              )}
                              {state?.status || 'Status unavailable'}
                            </div>
                            <div style={S.tlLocation}>
                              <PinIcon />
                              {getStateLocation(state)}
                            </div>
                            {meta && <div style={S.tlMeta}>{meta}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* EXTERNAL TRACKING */}
              {externalTrackingLinks.length > 0 && (
                <div style={S.externalSection}>
                  <h4 style={{ fontFamily: "'Syne', sans-serif", fontSize: 16, fontWeight: 700, marginBottom: 12 }}>
                    Track with Carrier
                  </h4>
                  <div>
                    {externalTrackingLinks.map((ext, ei) => (
                      <a key={ei} href={ext?.url || '#'} target="_blank" rel="noopener noreferrer"
                        className="ext-link-hover" style={S.extLink}>
                        {ext?.title || 'Track with carrier'}
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                        </svg>
                      </a>
                    ))}
                  </div>
                </div>
              )}

            </div>
          );
        })}
      </div>

      <Footer />
    </div>
  );
}

export default TrackingInterface;